import React, { useEffect, useRef } from 'react';
import { AlertTriangle, Lock } from 'lucide-react';

/**
 * Step title block. Moves focus to the heading on mount so screen readers and
 * keyboard users land at the top of the new step.
 */
const StepHeading = ({ title, description, flaggedMessage, locked }) => {
  const ref = useRef(null);
  useEffect(() => {
    ref.current?.focus({ preventScroll: true });
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  return (
    <div className="mb-5">
      <h1 ref={ref} tabIndex={-1} className="text-xl font-bold text-brand-dark focus:outline-none sm:text-2xl">{title}</h1>
      {description && <p className="mt-1 text-sm text-gray-600">{description}</p>}
      {flaggedMessage && (
        <div role="alert" className="mt-3 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" aria-hidden="true" />
          <div>
            <p className="font-semibold">The reviewer asked you to update this section</p>
            <p className="mt-0.5 whitespace-pre-line">{flaggedMessage}</p>
          </div>
        </div>
      )}
      {locked && (
        <div className="mt-3 flex items-start gap-2 rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
          <Lock className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-500" aria-hidden="true" />
          <p>This section is locked while the reviewer's requested changes are completed. You can still read it.</p>
        </div>
      )}
    </div>
  );
};

export const SectionCard = ({ title, description, children, className = '' }) => (
  <section className={`rounded-xl border border-gray-200 bg-white p-4 shadow-sm sm:p-6 ${className}`}>
    {title && <h2 className="text-base font-semibold text-gray-900 sm:text-lg">{title}</h2>}
    {description && <p className="mt-1 text-sm text-gray-600">{description}</p>}
    <div className={title || description ? 'mt-4' : ''}>{children}</div>
  </section>
);

export const InfoNotice = ({ title, children, tone = 'info' }) => {
  const palette = tone === 'warning'
    ? 'border-amber-200 bg-amber-50 text-amber-900'
    : 'border-blue-200 bg-blue-50 text-blue-900';
  return (
    <div className={`rounded-lg border p-3 text-sm ${palette}`}>
      {title && <p className="font-semibold">{title}</p>}
      <div className={title ? 'mt-1' : ''}>{children}</div>
    </div>
  );
};

export default StepHeading;
